import Phaser from 'phaser';
import { inject as injectVercelAnalytics } from '@vercel/analytics';
import { BootScene } from './scenes/BootScene.js';
import { MainMenuScene } from './scenes/MainMenuScene.js';
import { HowToPlayScene } from './scenes/HowToPlayScene.js';
import { PerkSelectScene } from './scenes/PerkSelectScene.js';
import { GameScene } from './scenes/GameScene.js';
import { HUDScene } from './scenes/HUDScene.js';
import { GameOverScene } from './scenes/GameOverScene.js';
import { ChestCeremonyScene } from './scenes/ChestCeremonyScene.js';
import { AgentInfoScene } from './scenes/AgentInfoScene.js';
import { SkinSelectScene } from './scenes/SkinSelectScene.js';
import { CRTPipeline } from './effects/CRTPipeline.js';
import { initAuth } from './auth/auth-ui.js';
import { setMusicEnabled, isMusicEnabled, onMusicEnabledChange } from './audio/Music.js';
import { setSfxEnabled, isSfxEnabled, onSfxEnabledChange } from './audio/SFX.js';
import { setCalmMode, isCalmMode, onCalmModeChange } from './audio/CalmMode.js';
import { installCalmCameraGuard } from './effects/calm-camera-guard.js';
import { isSpectateMode, applySpectatorChrome } from './spectate.js';
import { initSponsorPoolPage } from './payments/sponsor-pool-page.js';
import { initSponsorChestPage } from './payments/sponsor-chest-page.js';

// Standalone HTML pages (sponsor flows) hijack the page; skip Phaser
// boot + auth wiring when one of them claimed the location.
const isStandalonePage = initSponsorPoolPage() || initSponsorChestPage();

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  pixelArt: true,
  roundPixels: true,
  antialias: false,
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: 960,
    height: 640,
  },
  backgroundColor: '#080810',
  scene: [BootScene, MainMenuScene, HowToPlayScene, PerkSelectScene, GameScene, HUDScene, GameOverScene, ChestCeremonyScene, AgentInfoScene, SkinSelectScene],
};

const game = isStandalonePage ? null : new Phaser.Game(config);

// Globally gate camera shakes/flashes behind Calm Mode (single install point).
if (game) installCalmCameraGuard();

// Vercel Analytics — no-op outside the Vercel runtime, so devving locally
// won't ping anything. Resolves /_vercel/insights/* once deployed.
injectVercelAnalytics();

// Register the CRT post-FX pipeline (post pipelines aren't typed in
// GameConfig; register via the renderer's pipeline manager after boot).
if (game) {
  game.events.once(Phaser.Core.Events.READY, () => {
    const renderer = game.renderer as Phaser.Renderer.WebGL.WebGLRenderer;
    renderer.pipelines.addPostPipeline('CRT', CRTPipeline);
  });
}

// Spectator mode (byte iframe embed): read-only. Skip auth + hide all the
// interactive chrome (wallet, profile, switches, leaderboard). The game boots
// normally; BootScene routes straight into the observer GameScene.
const spectating = !isStandalonePage && isSpectateMode();
if (spectating) applySpectatorChrome();

// Initialize auth (X/Twitter login) — skipped when spectating (no wallet UI).
if (!isStandalonePage && !spectating) initAuth();

// Wire the analog music switch in the TV bezel.
const musicSwitch = document.getElementById('music-switch');
if (musicSwitch && !isStandalonePage && !spectating) {
  const sync = (on: boolean) => musicSwitch.classList.toggle('on', on);
  sync(isMusicEnabled());
  onMusicEnabledChange(sync);
  musicSwitch.addEventListener('click', () => setMusicEnabled(!isMusicEnabled()));
}

// Wire the analog SFX switch (just left of the music switch).
const sfxSwitch = document.getElementById('sfx-switch');
if (sfxSwitch && !isStandalonePage && !spectating) {
  const sync = (on: boolean) => sfxSwitch.classList.toggle('on', on);
  sync(isSfxEnabled());
  onSfxEnabledChange(sync);
  sfxSwitch.addEventListener('click', () => setSfxEnabled(!isSfxEnabled()));
}

// Wire the Calm Mode switch (no-stimulus: −60% audio + no flashes/shakes).
const calmSwitch = document.getElementById('calm-switch');
if (calmSwitch && !isStandalonePage && !spectating) {
  const sync = (on: boolean) => calmSwitch.classList.toggle('on', on);
  sync(isCalmMode());
  onCalmModeChange(sync);
  calmSwitch.addEventListener('click', () => setCalmMode(!isCalmMode()));
}
